// src/pages/admin/InvenireClientsPage.jsx
// Added: Google Analytics tracking
// Added: textColor support for light backgrounds
// Updated: Standardized header with logo
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { trackPageView, trackClientUpdated } from "../../utils/analytics";

const API_BASE = process.env.REACT_APP_API_BASE || "https://api.invenire.app";

function prettyJson(obj) {
  try {
    return JSON.stringify(obj || {}, null, 2);
  } catch {
    return "{}";
  }
}

// Helper to detect if a color is light
function isLightColor(color) {
  if (!color) return false;
  let hex = color.replace('#', '');
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }
  if (!/^[0-9A-Fa-f]{6}$/.test(hex)) return false;
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5;
}

export default function InvenireClientsPage() {
  const navigate = useNavigate();

  const [tenants, setTenants] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  const [name, setName] = useState("");
  const [key, setKey] = useState("");

  const [companyName, setCompanyName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#ffb600");
  const [accentColor, setAccentColor] = useState("#002D72");
  const [backgroundColor, setBackgroundColor] = useState("#050a1a");
  const [textColor, setTextColor] = useState("#ffffff");
  const [autoTextColor, setAutoTextColor] = useState(true);
  const [headerGradient, setHeaderGradient] = useState(
    "linear-gradient(90deg, #ffb347 0%, #ff6a00 100%)"
  );
  const [buttonPrimaryBg, setButtonPrimaryBg] = useState("#17c4c7");
  const [buttonTextColor, setButtonTextColor] = useState("#041520");

  const [brandingText, setBrandingText] = useState("{}");

  const [introScript, setIntroScript] = useState("");
  const [endScript, setEndScript] = useState("");
  const [thankHeading, setThankHeading] = useState("");
  const [thankBody, setThankBody] = useState("");

  // Security settings (super admin only)
  const [requireAuth, setRequireAuth] = useState(true);

  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);

  // Track page view on mount
  useEffect(() => {
    document.title = 'Invenire - Client Management';
    trackPageView('/admin/clients', 'Client Management');
  }, []);

  // Auto-detect text color when background changes
  useEffect(() => {
    if (autoTextColor) {
      const suggestedTextColor = isLightColor(backgroundColor) ? '#1f2937' : '#ffffff';
      setTextColor(suggestedTextColor);
    }
  }, [backgroundColor, autoTextColor]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/tenants`);
        const data = await res.json();
        // Handle both array response and object with tenants property
        const list = Array.isArray(data) ? data : (data.tenants || []);
        setTenants(list);
        if (list.length > 0) {
          selectTenant(list[0]);
        }
      } catch (e) {
        setError(`Failed to load clients: ${String(e)}`);
      }
    })();
  }, []);

  function selectTenant(t) {
    setSelectedId(t.id);
    setError("");
    setStatus("");

    const b = t.branding || {};
    const ic = t.interview_config || {};

    setName(t.name || "");
    setKey(t.key || "");

    setCompanyName(b.companyName || t.name || t.key || "");
    setLogoUrl(b.logoUrl || "");
    setPrimaryColor(b.primaryColor || "#ffb347");
    setAccentColor(b.accentColor || "#ff6a00");
    setBackgroundColor(b.backgroundColor || "#050a1a");
    
    // Text color - check if explicitly set or should auto-detect
    if (b.textColor) {
      setTextColor(b.textColor);
      setAutoTextColor(false);
    } else {
      setAutoTextColor(true);
      setTextColor(isLightColor(b.backgroundColor || "#050a1a") ? '#1f2937' : '#ffffff');
    }
    
    setHeaderGradient(
      b.headerGradient || "linear-gradient(90deg, #ffb347 0%, #ff6a00 100%)"
    );
    setButtonPrimaryBg(b.buttonPrimaryBg || "#17c4c7");
    setButtonTextColor(b.buttonTextColor || "#041520");

    setBrandingText(prettyJson(b));

    setIntroScript(ic.intro_script || "");
    setEndScript(ic.end_script || "");
    setThankHeading(ic.thank_you_heading || "");
    setThankBody(ic.thank_you_body || "");
    
    // Security settings
    setRequireAuth(t.require_auth !== false); // Default to true
  }

  function openClientAdmin(tenantKey) {
    navigate(`/client/${tenantKey}`);
  }

  async function saveTenant(e) {
    e.preventDefault();
    setError("");
    setStatus("");
    setSaving(true);

    try {
      let branding = {};
      if (brandingText && brandingText.trim()) {
        try {
          branding = JSON.parse(brandingText);
          if (typeof branding !== "object" || branding === null) {
            throw new Error("Branding JSON must be an object.");
          }
        } catch (err) {
          setError(`Branding JSON invalid: ${err.message}`);
          setSaving(false);
          return;
        }
      }

      branding = {
        ...branding,
        companyName: companyName || name || key,
        logoUrl: logoUrl || undefined,
        primaryColor,
        accentColor,
        backgroundColor,
        textColor: autoTextColor ? undefined : textColor, // Only save if manually set
        headerGradient,
        buttonPrimaryBg,
        buttonTextColor,
      };

      const interview_config = {
        intro_script: introScript,
        end_script: endScript,
        thank_you_heading: thankHeading,
        thank_you_body: thankBody,
      };

      const isNew = !selectedId;
      const payload = {
        id: selectedId || null,
        name: name.trim(),
        key: key.trim(),
        branding,
        interview_config,
        require_auth: requireAuth,
      };

      if (!payload.key || !payload.name) {
        setError("Client key and name are required.");
        setSaving(false);
        return;
      }

      const res = await fetch(`${API_BASE}/tenants/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(`Save failed: ${JSON.stringify(data)}`);
      } else {
        // Track client update
        trackClientUpdated(data.key, isNew);
        
        setTenants((prev) => {
          const without = prev.filter((t) => t.id !== data.id);
          return [...without, data].sort((a, b) =>
            (a.name || a.key).localeCompare(b.name || b.key)
          );
        });
        setSelectedId(data.id);
        setStatus("Client saved.");
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setSaving(false);
    }
  }

  const selectedTenant = tenants.find(t => t.id === selectedId);
  
  // Preview colors
  const previewTextColor = autoTextColor 
    ? (isLightColor(backgroundColor) ? '#1f2937' : '#ffffff')
    : textColor;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.logoContainer}>
          <img 
            src="/invenire-logo-transparent.png" 
            alt="Invenire" 
            style={styles.logoImage}
          />
          <span style={styles.subtitle}>Client Management</span>
        </div>
        <button
          style={styles.backButton}
          type="button"
          onClick={() => navigate("/admin")}
        >
          ← Admin Home
        </button>
      </div>

      <div style={styles.content}>
        {/* Hero */}
        <div style={styles.hero}>
          <h1 style={styles.heroTitle}>Invenire Client Management</h1>
          <p style={styles.heroText}>
            Manage all client configurations across the platform. Each client has their own
            branding, scripts, and admin portal.
          </p>
        </div>

        <div style={styles.grid}>
          {/* Left: list of tenants */}
          <div style={styles.card}>
            <div style={styles.tenantListHeader}>
              <h2 style={styles.tenantListTitle}>Clients</h2>
              <button
                style={styles.addButton}
                onClick={() => {
                  setSelectedId(null);
                  setName("");
                  setKey("");
                  setCompanyName("");
                  setPrimaryColor("#3ECF8E");
                  setAccentColor("#1E40AF");
                  setBackgroundColor("#0D1117");
                  setTextColor("#ffffff");
                  setAutoTextColor(true);
                  setHeaderGradient("linear-gradient(135deg, #11181C 0%, #1a2632 100%)");
                  setButtonPrimaryBg("#3ECF8E");
                  setButtonTextColor("#11181C");
                  setBrandingText("{}");
                  setIntroScript("");
                  setEndScript("");
                  setThankHeading("");
                  setThankBody("");
                  setRequireAuth(true);
                  setError("");
                  setStatus("");
                }}
              >
                + Add Client
              </button>
            </div>
            <ul style={styles.tenantList}>
              {tenants.map((t) => (
                <li
                  key={t.id}
                  style={{
                    ...styles.tenantItem,
                    backgroundColor: t.id === selectedId ? "rgba(23,196,199,0.18)" : "transparent",
                    border: t.id === selectedId ? "1px solid rgba(23,196,199,0.7)" : "1px solid transparent",
                  }}
                  onClick={() => selectTenant(t)}
                >
                  <div style={styles.tenantItemContent}>
                    <div>
                      <div style={styles.tenantName}>{t.name || t.key}</div>
                      <div style={styles.tenantKey}>{t.key}</div>
                    </div>
                    <button
                      style={styles.tenantAdminButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        openClientAdmin(t.key);
                      }}
                    >
                      Admin →
                    </button>
                  </div>
                </li>
              ))}
              {tenants.length === 0 && (
                <li style={styles.emptyTenants}>No clients yet.</li>
              )}
            </ul>
          </div>

          {/* Right: form */}
          <div style={styles.formCard}>
            <div style={styles.formHeader}>
              <h2 style={styles.formTitle}>
                {selectedId ? `Edit: ${selectedTenant?.name || key}` : "Create Client"}
              </h2>
              {selectedId && selectedTenant?.key && (
                <button
                  style={styles.openAdminButton}
                  onClick={() => openClientAdmin(selectedTenant.key)}
                >
                  Open {selectedTenant.name} Admin →
                </button>
              )}
            </div>

            <form onSubmit={saveTenant}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Client Key (e.g. "pwc")</label>
                <input
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Client Name</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={styles.input}
                />
              </div>

              <h3 style={styles.sectionTitle}>Branding (basic)</h3>
              <div style={styles.formGroup}>
                <label style={styles.label}>Company Name</label>
                <input
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Logo URL</label>
                <small style={styles.hint}>
                  Used in email notifications. Recommended: 200x50px PNG with transparent background.
                </small>
                <input
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  placeholder="https://example.com/logo.png"
                  style={styles.input}
                />
                {logoUrl && (
                  <div style={styles.logoPreview}>
                    <img 
                      src={logoUrl} 
                      alt="Logo preview" 
                      style={{ maxHeight: 40, maxWidth: 200 }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
              </div>
              <div style={styles.colorRow}>
                <div>
                  <label style={styles.label}>Primary Color</label>
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    style={styles.colorInput}
                  />
                </div>
                <div>
                  <label style={styles.label}>Accent Color</label>
                  <input
                    type="color"
                    value={accentColor}
                    onChange={(e) => setAccentColor(e.target.value)}
                    style={styles.colorInput}
                  />
                </div>
                <div>
                  <label style={styles.label}>Button Primary Bg</label>
                  <input
                    type="color"
                    value={buttonPrimaryBg}
                    onChange={(e) => setButtonPrimaryBg(e.target.value)}
                    style={styles.colorInput}
                  />
                </div>
                <div>
                  <label style={styles.label}>Button Text Color</label>
                  <input
                    type="color"
                    value={buttonTextColor}
                    onChange={(e) => setButtonTextColor(e.target.value)}
                    style={styles.colorInput}
                  />
                </div>
              </div>
              
              {/* Background and Text Color Row */}
              <div style={styles.bgTextRow}>
                <div>
                  <label style={styles.label}>Background Color</label>
                  <input
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    style={styles.colorInput}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={styles.labelWithCheckbox}>
                    Text Color
                    <label style={styles.autoDetectLabel}>
                      <input
                        type="checkbox"
                        checked={autoTextColor}
                        onChange={(e) => setAutoTextColor(e.target.checked)}
                      />
                      Auto-detect
                    </label>
                  </label>
                  <div style={styles.textColorInput}>
                    <input
                      type="color"
                      value={textColor}
                      onChange={(e) => {
                        setTextColor(e.target.value);
                        setAutoTextColor(false);
                      }}
                      disabled={autoTextColor}
                      style={{ ...styles.colorInput, opacity: autoTextColor ? 0.5 : 1 }}
                    />
                    <span style={styles.textColorLabel}>
                      {autoTextColor ? "(auto)" : textColor}
                    </span>
                  </div>
                </div>
                
                {/* Live Preview */}
                <div style={{ 
                  ...styles.preview,
                  background: backgroundColor,
                  color: previewTextColor,
                }}>
                  <div style={styles.previewLabel}>Preview</div>
                  <div style={styles.previewTitle}>Welcome!</div>
                  <div style={styles.previewText}>Sample text</div>
                </div>
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>Header Gradient</label>
                <input
                  value={headerGradient}
                  onChange={(e) => setHeaderGradient(e.target.value)}
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Branding JSON (advanced)</label>
                <small style={styles.hint}>
                  Merged with the basic fields above. Must be valid JSON.
                </small>
                <textarea
                  value={brandingText}
                  onChange={(e) => setBrandingText(e.target.value)}
                  rows={6}
                  style={styles.textarea}
                />
              </div>

              <h3 style={styles.sectionTitle}>Interview Scripts</h3>
              <div style={styles.formGroup}>
                <label style={styles.label}>Intro Script</label>
                <small style={styles.hint}>
                  Tokens: {"{greeting}"}, {"{candidate_first_name}"},{" "}
                  {"{role_title}"}, {"{company_name}"}.
                </small>
                <textarea
                  value={introScript}
                  onChange={(e) => setIntroScript(e.target.value)}
                  rows={4}
                  style={styles.textarea}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>End / Wrap-Up Script</label>
                <textarea
                  value={endScript}
                  onChange={(e) => setEndScript(e.target.value)}
                  rows={3}
                  style={styles.textarea}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Thank You Heading</label>
                <input
                  value={thankHeading}
                  onChange={(e) => setThankHeading(e.target.value)}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Thank You Body</label>
                <textarea
                  value={thankBody}
                  onChange={(e) => setThankBody(e.target.value)}
                  rows={3}
                  style={styles.textarea}
                />
              </div>

              <h3 style={styles.securityTitle}>
                🔒 Security Settings
                <span style={styles.superAdminBadge}>SUPER ADMIN ONLY</span>
              </h3>
              <div style={styles.securityBox}>
                <label style={styles.securityLabel}>
                  <input
                    type="checkbox"
                    checked={requireAuth}
                    onChange={(e) => setRequireAuth(e.target.checked)}
                    style={styles.securityCheckbox}
                  />
                  <div>
                    <div style={styles.securityLabelText}>Require Authentication</div>
                    <div style={styles.securityHint}>
                      When enabled, users must sign in with a registered and approved email address and password, Google or Microsoft to access the client admin portal.
                      Disable for demo/public clients.
                    </div>
                  </div>
                </label>
              </div>

              <button style={styles.saveButton} disabled={saving}>
                {saving ? "Saving..." : "Save Client"}
              </button>
            </form>

            {error && (
              <pre style={styles.errorText}>{error}</pre>
            )}
            {status && (
              <div style={styles.statusText}>{status}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  container: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #11181C 0%, #1a2632 50%, #0a0f14 100%)",
    color: "#fff",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 40px",
    borderBottom: "1px solid rgba(255,255,255,0.1)"
  },
  logoContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 4
  },
  logoImage: {
    height: 48,
    width: "auto",
    objectFit: "contain"
  },
  subtitle: {
    fontSize: 14,
    color: "#9ca3af"
  },
  backButton: {
    padding: "8px 16px",
    background: "rgba(255,255,255,0.1)",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    fontSize: 14,
    cursor: "pointer"
  },
  content: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "40px 20px"
  },
  hero: {
    marginBottom: 24
  },
  heroTitle: {
    fontSize: "1.9rem",
    marginBottom: 6
  },
  heroText: {
    color: "#9ca3af",
    fontSize: "0.98rem",
    maxWidth: 640
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "280px 1fr",
    gap: 20,
    marginTop: 16
  },
  card: {
    background: "rgba(15, 23, 42, 0.9)",
    borderRadius: 18,
    padding: 14,
    border: "1px solid rgba(30, 64, 175, 0.6)",
    boxShadow: "0 18px 45px rgba(15, 23, 42, 0.75)"
  },
  tenantListHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12
  },
  tenantListTitle: {
    margin: 0,
    fontSize: "1rem"
  },
  addButton: {
    padding: "6px 12px",
    fontSize: "0.8rem",
    background: "#17c4c7",
    color: "#11181C",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    fontWeight: 600
  },
  tenantList: {
    listStyle: "none",
    paddingLeft: 0,
    margin: 0
  },
  tenantItem: {
    padding: "6px 8px",
    borderRadius: 6,
    cursor: "pointer",
    marginBottom: 4
  },
  tenantItemContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  tenantName: {
    fontWeight: 600
  },
  tenantKey: {
    fontSize: "0.8rem",
    opacity: 0.7
  },
  tenantAdminButton: {
    padding: "2px 8px",
    fontSize: "0.75rem",
    background: "rgba(255,255,255,0.1)",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    cursor: "pointer"
  },
  emptyTenants: {
    opacity: 0.7,
    padding: "8px 0"
  },
  formCard: {
    background: "rgba(15, 23, 42, 0.9)",
    borderRadius: 18,
    padding: 18,
    border: "1px solid rgba(30, 64, 175, 0.6)",
    boxShadow: "0 18px 45px rgba(15, 23, 42, 0.75)"
  },
  formHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16
  },
  formTitle: {
    margin: 0
  },
  openAdminButton: {
    padding: "6px 12px",
    background: "#17c4c7",
    color: "#11181C",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 14
  },
  formGroup: {
    marginBottom: 10
  },
  label: {
    display: "block",
    marginBottom: 4,
    color: "#e5e7eb",
    fontSize: 14
  },
  hint: {
    display: "block",
    opacity: 0.7,
    fontSize: "0.85rem",
    marginBottom: 4
  },
  input: {
    width: "100%",
    padding: "8px 12px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 6,
    color: "#fff",
    fontSize: 14,
    boxSizing: "border-box"
  },
  textarea: {
    width: "100%",
    padding: "8px 12px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 6,
    color: "#fff",
    fontSize: 14,
    fontFamily: "monospace",
    boxSizing: "border-box",
    resize: "vertical"
  },
  colorRow: {
    display: "flex",
    gap: 16,
    marginBottom: 10,
    flexWrap: "wrap"
  },
  colorInput: {
    display: "block",
    marginTop: 4,
    cursor: "pointer"
  },
  logoPreview: {
    marginTop: 8,
    padding: 8,
    background: "#1a1a2e",
    borderRadius: 4
  },
  bgTextRow: {
    display: "flex",
    gap: 16,
    marginBottom: 10,
    padding: 12,
    background: "rgba(255,255,255,0.05)",
    borderRadius: 8,
    alignItems: "flex-start"
  },
  labelWithCheckbox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 4,
    color: "#e5e7eb",
    fontSize: 14
  },
  autoDetectLabel: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.8rem",
    fontWeight: "normal",
    cursor: "pointer"
  },
  textColorInput: {
    display: "flex",
    gap: 8,
    marginTop: 4,
    alignItems: "center"
  },
  textColorLabel: {
    fontSize: "0.85rem",
    opacity: 0.7
  },
  preview: {
    minWidth: 120,
    padding: 12,
    borderRadius: 6,
    textAlign: "center",
    border: "1px solid rgba(255,255,255,0.2)"
  },
  previewLabel: {
    fontSize: "0.7rem",
    opacity: 0.7,
    marginBottom: 4
  },
  previewTitle: {
    fontWeight: 600
  },
  previewText: {
    fontSize: "0.8rem",
    opacity: 0.7
  },
  sectionTitle: {
    marginTop: 16,
    marginBottom: 10,
    fontSize: "1rem",
    color: "#e5e7eb"
  },
  securityTitle: {
    marginTop: 16,
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: "1rem",
    color: "#e5e7eb"
  },
  superAdminBadge: {
    fontSize: "0.7rem",
    background: "#ef4444",
    color: "#fff",
    padding: "2px 6px",
    borderRadius: 4,
    fontWeight: 500
  },
  securityBox: {
    marginBottom: 16,
    padding: 12,
    background: "rgba(239, 68, 68, 0.1)",
    borderRadius: 8,
    border: "1px solid rgba(239, 68, 68, 0.3)"
  },
  securityLabel: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer"
  },
  securityCheckbox: {
    width: 18,
    height: 18,
    cursor: "pointer"
  },
  securityLabelText: {
    fontWeight: 600
  },
  securityHint: {
    fontSize: "0.85rem",
    opacity: 0.7,
    marginTop: 2
  },
  saveButton: {
    padding: "10px 20px",
    background: "#17c4c7",
    color: "#11181C",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 14
  },
  errorText: {
    color: "#f87171",
    marginTop: 12,
    whiteSpace: "pre-wrap"
  },
  statusText: {
    marginTop: 8,
    color: "#9ca3af"
  }
};
